import React, { useState } from 'react';
import { Form, Input, Button, Card, message, Divider } from 'antd';
import { MailOutlined, LockOutlined, IdcardOutlined, ArrowLeftOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { loginSuccess } from '../../store/slices/authSlice';
import api from '../../utils/api';

const RegisterPage: React.FC = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const handleRegister = async (values: any) => {
    setLoading(true);
    try {
      const response = await api.post('/api/auth/register', {
        email: values.email,
        password: values.password,
        license: {
          number: values.licenseNumber
        }
      });

      if (response.data.success) {
        message.success('登録完了しました！');
        
        // 保存用户信息到Redux
        const { user, token } = response.data.data;
        localStorage.setItem('token', token);
        dispatch(loginSuccess(user));
        
        // 跳转回首页或租车页面
        navigate('/');
      }
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || '登録中にエラーが発生しました';
      message.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleBackToLogin = () => {
    navigate('/login');
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: 20
    }}>
      <Card
        style={{
          width: '100%',
          maxWidth: 400,
          boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
          borderRadius: 12
        }}
      >
        <div style={{ textAlign: 'center', marginBottom: 30 }}>
          <h1 style={{ color: '#1890ff', fontSize: 28, fontWeight: 'bold', margin: 0 }}>
            AES Mobility
          </h1>
          <p style={{ color: '#666', margin: '8px 0 0 0' }}>新規ユーザー登録</p>
        </div>

        <Form
          form={form}
          onFinish={handleRegister}
          layout="vertical"
          size="large"
        >
          <Form.Item
            name="email"
            label="メールアドレス"
            rules={[
              { required: true, message: 'メールアドレスを入力してください' },
              { type: 'email', message: '正しいメールアドレスを入力してください' }
            ]}
          >
            <Input
              prefix={<MailOutlined />}
              placeholder="メールアドレスを入力"
            />
          </Form.Item>

          <Form.Item
            name="password"
            label="パスワード"
            rules={[
              { required: true, message: 'パスワードを入力してください' },
              { min: 8, message: 'パスワードは8文字以上で入力してください' },
              { 
                pattern: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/, 
                message: 'パスワードは大文字、小文字、数字を含む必要があります' 
              }
            ]}
          >
            <Input.Password
              prefix={<LockOutlined />}
              placeholder="パスワードを入力"
            />
          </Form.Item>

          <Form.Item
            name="confirmPassword"
            label="パスワード確認"
            dependencies={['password']}
            rules={[
              { required: true, message: 'パスワード確認を入力してください' },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue('password') === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error('パスワードが一致しません'));
                },
              }),
            ]}
          >
            <Input.Password
              prefix={<LockOutlined />}
              placeholder="パスワードを再入力"
            />
          </Form.Item>

          <Form.Item
            name="licenseNumber"
            label="運転免許証番号"
            rules={[
              { required: true, message: '運転免許証番号を入力してください' },
              { min: 1, message: '運転免許証番号を入力してください' },
              { max: 50, message: '運転免許証番号は50文字以下で入力してください' }
            ]}
          >
            <Input
              prefix={<IdcardOutlined />}
              placeholder="運転免許証番号を入力"
              maxLength={50}
            />
          </Form.Item>

          <Form.Item>
            <Button
              type="primary"
              htmlType="submit"
              loading={loading}
              block
              style={{ height: 48, fontSize: 16, marginBottom: 16 }}
            >
              登録する
            </Button>

            <Button
              icon={<ArrowLeftOutlined />}
              onClick={handleBackToLogin}
              block
              style={{ height: 40 }}
            >
              ログインページに戻る
            </Button>
          </Form.Item>
        </Form>

        <Divider />

        <div style={{ textAlign: 'center', color: '#666', fontSize: 12 }}>
          <p>登録することで、</p>
          <p>
            <a href="#" style={{ color: '#1890ff' }}>利用規約</a>
            と
            <a href="#" style={{ color: '#1890ff' }}>プライバシーポリシー</a>
            に同意したものとみなします
          </p>
        </div>
      </Card>
    </div>
  );
};

export default RegisterPage;